import { useQueryClient } from '@tanstack/react-query';
import { FC, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';

import Button from '@/components/Button';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import Card from '@/pages/MLProject/components/Card';
import { MLProjectTranslation } from '@/types/resources';
import { api } from '@/utils/api';
import { MLProjectTranslationStatus } from '@/utils/enums';
import { humanFileSize } from '@/utils/helpers';

import GraphicElementsForm from './GraphicElementsForm';

const MergeButton = styled(Button)`
  width: 100%;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Box = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  padding: 24px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const Label = styled(Text).attrs({ $type: 'caption' })`
  margin: 0 0 8px;
`;

interface AssetProps {
  $active: boolean;
  $clickable?: boolean;
  $invalid?: boolean;
}

const Asset = styled(Text).attrs({ $type: 'smallBold' })<AssetProps>`
  border-radius: 4px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  padding: 0 16px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.purple : theme.colors.grayscaleLight};
  background: ${({ theme }) => theme.colors.grayscaleLight};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.purple : theme.colors.gray};
  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
  ${({ $invalid, theme }) =>
    $invalid &&
    css`
      background: transparent;
      border-color: ${theme.colors.error};
      color: ${theme.colors.error};
    `}
`;

const VideoBox = styled(Box)`
  padding: 16px;
  gap: 24px;
  align-items: center;
`;

const VideoFileDescription = styled(Text)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  a {
    color: ${({ theme }) => theme.colors.purple};
    text-decoration: underline;
  }
  span {
    color: gray;
  }
`;

interface Props {
  translation: MLProjectTranslation;
}

const AssetsCard: FC<Props> = ({ translation }) => {
  const alerts = useAlerts();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const voiceoversMap = useMemo(() => {
    return translation.voiceovers!.reduce<{
      [uuid: string]: boolean;
    }>((acc, curr) => {
      return { ...acc, [curr.meta!.partUuid]: true };
    }, {});
  }, [translation.voiceovers]);

  const subtitlesMap = useMemo(() => {
    return translation.subtitles!.reduce<{
      [uuid: string]: boolean;
    }>((acc, curr) => {
      return { ...acc, [curr.meta!.partUuid]: true };
    }, {});
  }, [translation.subtitles]);

  const isMergeEnabled = useMemo(() => {
    if (translation.status === MLProjectTranslationStatus.DONE) {
      return false;
    }

    const unapprovedPart = translation.script!.find(
      (part) => !voiceoversMap[part.uuid],
    );

    return !unapprovedPart;
  }, [translation, voiceoversMap]);

  const onMerge = async () => {
    try {
      if (!translation.title || !translation.description) {
        return alerts.error('Please translate video title and description!');
      }

      const confirmed = await alerts.confirm(
        'Are you sure?',
        'Merge voiceovers to original video',
      );

      if (!confirmed) {
        return;
      }

      const { error } = await api.post<never, { error?: string }>(
        `/v1/distribution/ml-translations/${translation.id}/generate-video`,
      );

      if (error) {
        return alerts.error(error);
      }

      await queryClient.invalidateQueries([
        'ml_translations',
        {
          id: translation.id.toString(),
        },
      ]);

      alerts.success(
        'Final video is being generated',
        'Be patient - this job might take a while',
      );
    } catch (err) {
      alerts.error();
    }
  };

  const onGenerateVoiceovers = async () => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) {
        return;
      }

      const noVoice = translation.script?.find(
        (part) => !part.voiceoverSettings.voiceId,
      );

      if (noVoice) {
        return alerts.error(
          'Information missing',
          'Some of the parts does not have voice selected!',
        );
      }

      const res = await Promise.all(
        translation.script
          ?.filter(
            (part) => !voiceoversMap[part.uuid] && part.selectedTranslation,
          )
          .map((part) =>
            api.post<never, { error?: string }>(
              `/v1/distribution/ml-translations/${translation.id}/parts/${part.uuid}/voiceovers`,
            ),
          ) ?? [],
      );

      await queryClient.invalidateQueries([
        'ml_translations',
        {
          id: translation.id.toString(),
        },
      ]);

      if (res.find((job) => job.error)) {
        return alerts.error(
          'Failed to generate some of the voiceovers!',
          'Please generate them separately',
        );
      }

      alerts.close();
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <Card
      storageKey={`ml_translation_${translation.id}_assets_card_visibility`}
      title="Assets & original video"
      description="Merge original video with generated voiceovers and subtitles"
      defaultIsOpened
    >
      <Grid>
        <div>
          <Label>Original video</Label>
          <VideoBox>
            <svg
              width="88"
              height="88"
              viewBox="0 0 88 88"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                width="88"
                height="88"
                rx="4"
                fill={theme.colors.grayscaleLight}
              />
              <path
                d="M31 35.5H47C48.0609 35.5 49.0783 35.9214 49.8284 36.6716C50.5786 37.4217 51 38.4391 51 39.5V51.5C51 51.7652 50.8946 52.0196 50.7071 52.2071C50.5196 52.3946 50.2652 52.5 50 52.5H34C32.9391 52.5 31.9217 52.0786 31.1716 51.3284C30.4214 50.5783 30 49.5609 30 48.5V36.5C30 36.2348 30.1054 35.9804 30.2929 35.7929C30.4804 35.6054 30.7348 35.5 31 35.5V35.5Z"
                stroke={theme.colors.black}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M51 42L58 38V50L51 46"
                stroke={theme.colors.black}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <VideoFileDescription $type="smallNormal" as="div">
              <p>{translation.project!.video.name}</p>
              <div>
                <a
                  href={translation.project!.video.versions.original}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Download
                </a>
                &nbsp;&nbsp;&nbsp;
                <span>{humanFileSize(translation.project!.video.size)}</span>
              </div>
            </VideoFileDescription>
          </VideoBox>
        </div>
        <div>
          <Label>Prepared Voiceovers</Label>
          <Box>
            {translation.script!.map((part) => (
              <Asset
                onClick={() => {
                  const top =
                    document.getElementById(`ml-script-part-${part.uuid}`)!
                      .offsetTop - 50;
                  document
                    .getElementById('dashboard-scrollable-container')!
                    .scrollTo({
                      top,
                      behavior: 'smooth',
                    });
                }}
                $active={!!voiceoversMap[part.uuid]}
                $invalid={!part.selectedTranslation}
                $clickable
                key={part.uuid}
              >
                {part.timestamp}
              </Asset>
            ))}
          </Box>
        </div>
        <div>
          <Label>Prepared Subtitles</Label>
          <Box>
            {translation.script!.map((part) => (
              <Asset
                key={part.uuid}
                $active={
                  part.applySubtitles && (subtitlesMap[part.uuid] ?? false)
                }
              >
                {part.timestamp}
              </Asset>
            ))}
          </Box>
        </div>
        <div>
          <Label>Graphic Elements</Label>
          <GraphicElementsForm
            translationId={translation.id}
            applyTapForSoungImg={
              translation.settings?.applyTapForSoungImg ?? false
            }
          />
        </div>
        {Object.keys(voiceoversMap).length !== translation.script?.length && (
          <MergeButton onClick={onGenerateVoiceovers} color="secondary">
            Generate{translation.voiceovers?.length ? ' missing ' : ' '}
            voiceovers
          </MergeButton>
        )}
        <MergeButton disabled={!isMergeEnabled} onClick={onMerge}>
          Merge voiceovers to original video
        </MergeButton>
      </Grid>
    </Card>
  );
};

export default AssetsCard;
